/**
 * Desktop direct-to-Azure backend routing (2026-05-22).
 *
 * WHY: the desktop wrapper loads the deployed SWA, so by default every API call
 * goes same-origin to the SWA `/api/*` managed-Functions proxy. That proxy
 * BUFFERS responses (`await resp.text()`), so the chat SSE stream never streams
 * — the client sees pure "thinking" for the whole turn, then a one-shot dump —
 * and the long silent gap makes the Azure gateway RETRY the POST, which runs the
 * turn twice (the duplicate answers users saw). Measured: TTFB == total on the
 * deployed chat endpoint, confirming full buffering.
 *
 * FIX: in the DESKTOP build only, point the API client + the chat SSE consumer
 * at the Azure Container Apps backend directly (it streams SSE natively and has
 * no buffering proxy in front). Both `src/api/client.ts` and `src/chat/chatApi.ts`
 * already read `window.__ALETHEIA__.{apiBaseUrl, loopbackToken}` — we just
 * populate it. The bearer token comes from the OS keychain via the existing
 * `reveal_token` IPC.
 *
 * CRITICAL: only switch to direct mode WHEN a token is available. Setting
 * `apiBaseUrl` to Azure without a token would send unauthenticated requests
 * straight to the backend (401) — worse than the proxy. With no token (e.g. a
 * fresh install before sign-in/mint) we leave `__ALETHEIA__` untouched so calls
 * keep flowing through the SWA proxy (which injects the token server-side).
 * Call this again right after a token is minted to switch over.
 *
 * No-op in the browser build (`isDesktopApp()` false): a web bundle must never
 * carry a backend bearer token, so web stays on the same-origin proxy.
 */

import { isDesktopApp } from './isDesktopApp';
import { revealMcpToken, storeMcpToken } from './desktopIpc';
import { callEndpoint, getLogger } from '@/api';

const log = getLogger('desktop.direct');

/** The `system/mcp_token/mint` response — only the plaintext token matters here. */
interface MintResponse {
  token?: string;
}

/**
 * Return a usable MCP bearer token for the desktop, minting one if the keychain is empty.
 *
 * The desktop's direct-to-Azure routing needs a bearer the backend accepts — the per-device
 * MCP token (the API auth provider verifies it via the mcp_token repo). Before 2026-05-22 the
 * token only existed if the user manually minted one in Settings → MCP, so most desktops had
 * NONE → direct routing never engaged → chat stayed on the buffering proxy (no streaming,
 * gateway-retry double-posts). We now auto-mint on first launch: the mint call goes through
 * the same-origin SWA proxy (which injects the backend token server-side, so it works before
 * the keychain has anything), and we persist the result to the OS keychain via the existing
 * IPC. Best-effort — a mint failure just returns null and we stay on the proxy.
 */
async function ensureDesktopToken(workspaceId: string): Promise<string | null> {
  // v0.1.14: storage is workspace-keyed. We look up THIS workspace's stored
  // token; if absent, auto-mint via the same-origin SWA proxy (which uses
  // Clerk session auth to identify the user, then mints into their current
  // workspace) and persist the result under the workspace key.
  try {
    const existing = await revealMcpToken(workspaceId);
    if (existing) return existing;
  } catch {
    /* IPC denied / no token — fall through to mint via the proxy */
  }
  try {
    const resp = await callEndpoint<MintResponse>('POST', 'system/mcp_token/mint', {
      body: { label: 'aletheia-desktop (auto)' },
    });
    if (resp.token) {
      try {
        await storeMcpToken(workspaceId, resp.token);
      } catch {
        /* credential store unavailable — token still authorizes this session */
      }
      log.info('desktop_token_auto_minted', {});
      return resp.token;
    }
  } catch (err) {
    log.warn('desktop_token_auto_mint_failed', { error: String(err) });
  }
  return null;
}

/**
 * The Azure Container Apps ingress for the backend. Mirrors
 * `desktop/src-tauri/src/auth.rs::CLOUD_BACKEND_URL`. Not a secret — it's a
 * public ingress host; the bearer token (keychain) is what authorizes calls.
 */
const DESKTOP_BACKEND_URL =
  'https://ca-aletheia-backend.victoriouscoast-cd563405.centralus.azurecontainerapps.io';

interface AletheiaGlobals {
  loopbackToken?: string;
  apiBaseUrl?: string;
}

/**
 * Dedicated global for CHAT direct-routing ONLY. We deliberately do NOT touch the global
 * `window.__ALETHEIA__` that `client.ts` reads — non-chat calls stay on the same-origin SWA
 * proxy (proven, token hidden server-side), so a direct-Azure problem can never take down the
 * whole app (it did once: routing everything direct hit the CSP `connect-src` block and broke
 * every call). Only `chatApi.ts` reads `__ALETHEIA_CHAT__`, and only the chat SSE stream — the
 * one call that genuinely needs to bypass the buffering proxy — goes direct.
 */
const CHAT_GLOBAL = '__ALETHEIA_CHAT__';

/**
 * In the desktop app, route API calls directly to Azure (bypassing the buffering
 * SWA proxy) so chat streams and turns don't double-fire. Idempotent and safe to
 * call multiple times (after Clerk loads, and again after a token mint). Never
 * throws.
 *
 * Requires `workspaceId` (v0.1.14): token storage is workspace-keyed, so we
 * need to know which workspace's token to use. Calling without one — e.g.
 * before Clerk has loaded a user — is a no-op (chat stays on the proxy).
 */
export async function configureDesktopDirectBackend(workspaceId: string): Promise<void> {
  if (!isDesktopApp() || !workspaceId) return;
  try {
    const token = await ensureDesktopToken(workspaceId);
    if (!token) {
      // No token and mint failed — stay on the same-origin SWA proxy, which injects the
      // token server-side. The next launch retries the mint.
      log.info('desktop_direct_backend_skipped_no_token', {});
      return;
    }
    const w = window as unknown as Record<string, AletheiaGlobals | undefined>;
    // Chat-only direct routing — see CHAT_GLOBAL above. Non-chat calls keep using the proxy.
    w[CHAT_GLOBAL] = { apiBaseUrl: DESKTOP_BACKEND_URL, loopbackToken: token };
    log.info('desktop_direct_backend_enabled', { apiBaseUrl: DESKTOP_BACKEND_URL, scope: 'chat' });
  } catch (err) {
    // Credential store unavailable / IPC failure — leave the proxy path in place.
    log.warn('desktop_direct_backend_config_failed', { error: String(err) });
  }
}
